/**
 * Parse school-account CSV into POST /data/school-account payload shape.
 * Headers: school_id, value, grade, surname, firstname, chinese_name, birthday,
 * period_start_date (or typo eriod_start_date), period_end_date
 * Dates: dd/mm/yyyy (preferred) or yyyy-mm-dd.
 */

import { parseToIso, formatIsoToDdMmYyyy } from './dateDdMmYyyy';

const REQUIRED = [
  'school_id',
  'value',
  'grade',
  'surname',
  'firstname',
  'chinese_name',
  'birthday',
  'period_start_date',
  'period_end_date',
];

function stripBom(s) {
  if (!s) return s;
  return s.charCodeAt(0) === 0xfeff ? s.slice(1) : s;
}

function normalizeHeaderName(raw) {
  const k = stripBom(String(raw).trim().toLowerCase());
  if (k === 'eriod_start_date') return 'period_start_date';
  return k;
}

/** Split one CSV line; supports "quoted" fields and doubled quotes. */
export function parseCsvLine(line) {
  const out = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const c = line[i];
    if (inQuotes) {
      if (c === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cur += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      out.push(cur.trim());
      cur = '';
    } else {
      cur += c;
    }
  }
  out.push(cur.trim());
  return out;
}

function isValidOptionalBirthday(s) {
  if (!String(s ?? '').trim()) return true;
  return parseToIso(s) !== null;
}

/**
 * @param {string} text - full CSV file contents
 * @returns {{ success: true, payload: object, resultText: string } | { success: false, resultText: string }}
 */
export function parseSchoolAccountCsv(text) {
  const raw = stripBom(String(text ?? ''));
  const lines = raw.split(/\r?\n/).filter((ln) => String(ln).trim() !== '');
  if (lines.length === 0) {
    return { success: false, resultText: 'Empty file or no data rows.' };
  }

  const headerCells = parseCsvLine(lines[0]).map(normalizeHeaderName);
  const index = {};
  for (let i = 0; i < headerCells.length; i += 1) {
    const h = headerCells[i];
    if (h && index[h] === undefined) index[h] = i;
  }

  const missing = REQUIRED.filter((r) => index[r] === undefined);
  if (missing.length) {
    return {
      success: false,
      resultText: `Missing required column(s): ${missing.join(', ')}.\nExpected headers: ${REQUIRED.join(', ')} (period_start_date may be spelled eriod_start_date).`,
    };
  }

  const errors = [];
  /** @type {Array<{ schoolId: number, value: string, grade: number, surname: string, firstname: string, chinese_name: string, birthday: string, pStart: string, pEnd: string }>} */
  const rows = [];

  for (let li = 1; li < lines.length; li += 1) {
    const line = lines[li];
    const cells = parseCsvLine(line);
    const rowNum = li + 1;
    const get = (name) => {
      const idx = index[name];
      return idx !== undefined ? (cells[idx] ?? '') : '';
    };

    const schoolRaw = get('school_id').trim();
    const sid = parseInt(schoolRaw, 10);
    if (!schoolRaw || Number.isNaN(sid)) {
      errors.push(`Row ${rowNum}: school_id must be an integer.`);
      continue;
    }

    const value = String(get('value')).trim();
    if (!value) {
      errors.push(`Row ${rowNum}: value is required.`);
      continue;
    }

    const gradeRaw = String(get('grade')).trim();
    const grade = parseInt(gradeRaw, 10);
    if (Number.isNaN(grade)) {
      errors.push(`Row ${rowNum}: grade must be an integer.`);
      continue;
    }

    const pStartRaw = get('period_start_date').trim();
    const pEndRaw = get('period_end_date').trim();
    const pStart = pStartRaw ? parseToIso(pStartRaw) : null;
    const pEnd = pEndRaw ? parseToIso(pEndRaw) : null;
    if (!pStartRaw || !pStart) {
      errors.push(`Row ${rowNum}: invalid or empty period_start_date (use dd/mm/yyyy or yyyy-mm-dd).`);
      continue;
    }
    if (!pEndRaw || !pEnd) {
      errors.push(`Row ${rowNum}: invalid or empty period_end_date (use dd/mm/yyyy or yyyy-mm-dd).`);
      continue;
    }
    if (pEnd < pStart) {
      errors.push(`Row ${rowNum}: period_end_date must be on or after period_start_date.`);
      continue;
    }

    const birthdayRaw = get('birthday').trim();
    if (!isValidOptionalBirthday(birthdayRaw)) {
      errors.push(`Row ${rowNum}: invalid birthday (dd/mm/yyyy or yyyy-mm-dd, or leave empty).`);
      continue;
    }
    const birthdayNorm = birthdayRaw ? parseToIso(birthdayRaw) : '';

    rows.push({
      schoolId: sid,
      value,
      grade,
      surname: get('surname'),
      firstname: get('firstname'),
      chinese_name: get('chinese_name'),
      birthday: birthdayNorm || '',
      pStart,
      pEnd,
    });
  }

  if (errors.length) {
    return { success: false, resultText: errors.join('\n') };
  }

  if (rows.length === 0) {
    return { success: false, resultText: 'No data rows after the header.' };
  }

  const schoolId = rows[0].schoolId;
  for (let i = 0; i < rows.length; i += 1) {
    if (rows[i].schoolId !== schoolId) {
      return {
        success: false,
        resultText: `All rows must use the same school_id. Found ${schoolId} and ${rows[i].schoolId}.`,
      };
    }
  }

  const pStart0 = rows[0].pStart;
  const pEnd0 = rows[0].pEnd;
  for (let i = 0; i < rows.length; i += 1) {
    if (rows[i].pStart !== pStart0 || rows[i].pEnd !== pEnd0) {
      return {
        success: false,
        resultText: `All rows must share the same period_start_date (${pStart0}) and period_end_date (${pEnd0}). Row ${i + 2} differs.`,
      };
    }
  }

  const classOrder = [];
  const classKeyToIndex = new Map();
  for (let i = 0; i < rows.length; i += 1) {
    const r = rows[i];
    const key = `${r.value}\n${r.grade}`;
    if (!classKeyToIndex.has(key)) {
      classKeyToIndex.set(key, classOrder.length);
      classOrder.push({ value: r.value, grade: r.grade, students: [] });
    }
    const ci = classKeyToIndex.get(key);
    classOrder[ci].students.push({
      surname: r.surname,
      firstname: r.firstname,
      chineseName: r.chinese_name,
      birthday: r.birthday,
    });
  }

  const payload = {
    schoolId,
    periodStartDate: pStart0,
    periodEndDate: pEnd0,
    classes: classOrder.map((c) => ({
      value: c.value,
      grade: c.grade,
      students: c.students,
    })),
  };

  const classSummary = classOrder.map((c) => `"${c.value}" / grade ${c.grade} (${c.students.length} students)`).join('; ');
  const resultText = [
    `Parsed ${rows.length} data row(s).`,
    `School ID: ${schoolId}.`,
    `Period: ${formatIsoToDdMmYyyy(pStart0)} to ${formatIsoToDdMmYyyy(pEnd0)}.`,
    `Classes (${classOrder.length}): ${classSummary}.`,
    'Ready to create.',
  ].join('\n');

  return { success: true, payload, resultText };
}
